import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  numberAttribute,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { InfiniteScrollTriggerComponent } from '@vendasta/galaxy/infinite-scroll-trigger';
import { PagedListRequestInterface } from '@vendasta/galaxy/table';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import {
  CrmBoardColumnInterface,
  CrmBoardColumnMetadataInterface,
  CrmBoardDatasourceInterface,
  CrmBoardItemClickedEvent,
  CrmBoardItemClickedToken,
  CrmBoardItemCompareToken,
} from '../interfaces';
import { CrmBoardCardComponent } from '../card/card.component';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { DropItemEvent, ItemCacheMutation, ItemCacheOperation } from '../internal';

interface RequiredInputs<RowType, FilterType, SortType> {
  datasource: CrmBoardDatasourceInterface<RowType, FilterType, SortType>;
  column: CrmBoardColumnInterface<FilterType>;
}

@Component({
  selector: 'crm-board-column',
  templateUrl: './column.component.html',
  styleUrl: './column.component.scss',
  imports: [
    CommonModule,
    InfiniteScrollTriggerComponent,
    MatProgressBarModule,
    CrmBoardCardComponent,
    DragDropModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrmBoardColumnComponent<RowType, FilterType, SortType> implements OnInit {
  @Input({ required: true }) datasource?: CrmBoardDatasourceInterface<RowType, FilterType, SortType>;
  private _column?: CrmBoardColumnInterface<FilterType>;
  @Input({ required: true })
  set column(value: CrmBoardColumnInterface<FilterType>) {
    this._column = value;
    this.reset();
  }
  get column(): CrmBoardColumnInterface<FilterType> | undefined {
    return this._column;
  }
  @Input({ transform: numberAttribute }) pageSize = 5;
  @Input() headerTemplate?: TemplateRef<unknown>;
  @Input() cardTemplate?: TemplateRef<unknown>;
  @Output() itemDropped = new EventEmitter<DropItemEvent<RowType>>();
  @Output() itemEntered = new EventEmitter<void>();
  @Output() itemClicked = new EventEmitter<CrmBoardItemClickedEvent>();
  @Output() columnVisible = new EventEmitter<void>();
  @Output() columnHidden = new EventEmitter<void>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly compareFn? = inject(CrmBoardItemCompareToken, { optional: true });
  private readonly clickedFn? = inject(CrmBoardItemClickedToken, { optional: true });

  private fetchSubscription?: Subscription;

  private readonly cachedMutations$$ = new BehaviorSubject<ItemCacheMutation<RowType>[]>([]);
  private readonly items$$ = new BehaviorSubject<RowType[]>([]);
  protected readonly items$ = combineLatest([this.items$$, this.cachedMutations$$]).pipe(
    map(([items, mutations]) => this.applyMutations(items, mutations)),
  );
  private readonly metadata$$ = new BehaviorSubject<CrmBoardColumnMetadataInterface>({ hasMore: false });
  protected readonly metadata$ = combineLatest([this.metadata$$, this.cachedMutations$$]).pipe(
    map(([metadata, mutations]) => this.applyMetadataMutations(metadata, mutations)),
  );
  protected readonly hasMore$ = this.metadata$.pipe(
    map((m) => m.hasMore),
    distinctUntilChanged(),
  );
  protected readonly nextCursor$$ = new BehaviorSubject<string | null>(null);
  protected readonly loading$$ = new BehaviorSubject<boolean>(false);
  protected readonly loadMore$$ = new BehaviorSubject<null>(null);
  protected isDroppingTarget = false;

  ngOnInit() {
    const { datasource } = this.requireInputs();
    // load more
    combineLatest([this.hasMore$, this.loadMore$$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([hasMore]) => hasMore),
        debounceTime(100),
      )
      .subscribe({ next: () => this.loadMore() });
    // new search
    combineLatest([datasource.searchTerm$, datasource.filters$, datasource.sorting$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.reset() });
  }

  reset(): void {
    if (this.fetchSubscription?.closed === false) {
      this.fetchSubscription.unsubscribe();
    }
    this.items$$.next([]);
    this.cachedMutations$$.next([]);
    this.nextCursor$$.next(null);
    this.metadata$$.next({ hasMore: true });
    this.loading$$.next(false);
  }

  cacheMutation(operation: ItemCacheOperation, item: RowType): void {
    const mutation: ItemCacheMutation<RowType> = { operation, item };
    const currentMutations = this.cachedMutations$$.value;
    const existingMutationIndex = currentMutations.findIndex((m) => this.compareItems(m.item, item));
    if (existingMutationIndex >= 0) {
      // remove existing mutation if they offset, do nothing if they are the same
      const existingMutation = currentMutations[existingMutationIndex];
      if (existingMutation.operation !== operation) {
        currentMutations.splice(existingMutationIndex, 1);
      }
    } else {
      currentMutations.push(mutation);
    }
    this.cachedMutations$$.next([...currentMutations]);
  }

  protected canDropItem(): boolean {
    return this.column?.mutation !== undefined;
  }

  protected dropItemDropped(
    e: CdkDragDrop<CrmBoardColumnInterface<FilterType>, CrmBoardColumnInterface<FilterType>, RowType>,
  ): void {
    this.isDroppingTarget = false;
    const event: DropItemEvent<RowType> = {
      item: e.item.data,
      from: e.previousContainer.data.id,
      to: e.container.data.id,
    };
    if (event.from !== event.to) {
      this.itemDropped.emit(event);
    }
  }

  protected dropItemEntered(): void {
    this.isDroppingTarget = true;
    this.itemEntered.emit();
  }

  protected dropItemExited(): void {
    this.isDroppingTarget = false;
  }

  protected scrollAreaVisible(): void {
    this.loadMore$$.next(null);
  }

  protected loadMore(): void {
    const { datasource, column } = this.requireInputs();
    const req = combineLatest([
      datasource.searchTerm$,
      datasource.filters$,
      datasource.sorting$,
      this.nextCursor$$,
    ]).pipe(
      take(1),
      map(([searchTerm, viewFilters, sorting, cursor]) => {
        const columnFilters = column.filters ?? [];
        const compoundFilters = [...viewFilters, ...columnFilters];
        return {
          searchOptions: { text: searchTerm },
          filters: compoundFilters,
          sorting: sorting,
          pagingOptions: { cursor: cursor || '', pageSize: this.pageSize },
        } as PagedListRequestInterface<FilterType, SortType>;
      }),
      switchMap((request) => datasource.get(request)),
    );

    this.loading$$.next(true);
    this.fetchSubscription = req.subscribe({
      next: (res) => {
        this.items$$.next([...this.items$$.value, ...res.data]);
        this.metadata$$.next({
          hasMore: res.pagingMetadata.hasMore ?? false,
          totalResults: res.pagingMetadata.totalResults,
        });
        this.nextCursor$$.next(res.pagingMetadata?.nextCursor ?? null);
        this.loading$$.next(false);
      },
      error: (err) => {
        console.error(err);
        this.loading$$.next(false);
      },
    });
  }

  /** Validate all required inputs for strict template assertion */
  private requireInputs(): RequiredInputs<RowType, FilterType, SortType> {
    if (!this.datasource) {
      throw new Error('datasource is required');
    }
    if (!this.column) {
      throw new Error('column is required');
    }
    return { datasource: this.datasource, column: this.column };
  }

  private compareItems(a: RowType, b: RowType): boolean {
    if (this.compareFn) {
      return this.compareFn(a, b);
    }
    return JSON.stringify(a) === JSON.stringify(b);
  }

  private applyMutations(list: RowType[], mutations: ItemCacheMutation<RowType>[]): RowType[] {
    const addItems = mutations.filter((m) => m.operation === 'add').map((m) => m.item);
    const rmvItems = mutations.filter((m) => m.operation === 'remove').map((m) => m.item);
    return [...addItems, ...list].filter((item) => !rmvItems.some((ri) => this.compareItems(ri, item)));
  }

  private applyMetadataMutations(
    metadata: CrmBoardColumnMetadataInterface,
    mutations: ItemCacheMutation<RowType>[],
  ): CrmBoardColumnMetadataInterface {
    if (metadata.totalResults === undefined) {
      return metadata;
    }
    const addItems = mutations.filter((m) => m.operation === 'add').map((m) => m.item).length;
    const rmvItems = mutations.filter((m) => m.operation === 'remove').map((m) => m.item).length;
    const totalResults = Math.max(0, metadata.totalResults + addItems - rmvItems);
    return { ...metadata, totalResults };
  }

  protected handleCardClickEvent(item: RowType): void {
    if (this.clickedFn) {
      const event = this.clickedFn(item);
      this.itemClicked.emit(event);
    }
  }
}
